import type {
  DataResponse,
  PaginatedResponse,
  Task,
  TaskCancelRequest,
  TaskCreate,
  TaskSummary,
  TaskUpdate,
  TaskUserInput,
} from "@/types";
import { api } from "../api";

export const taskService = {
  list: (params?: { page?: number; pageSize?: number; status?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", String(params.page));
    if (params?.pageSize) searchParams.set("page_size", String(params.pageSize));
    if (params?.status) searchParams.set("status", params.status);
    return api.get<PaginatedResponse<TaskSummary>>(`/tasks?${searchParams}`);
  },
  get: (taskUuid: string) => api.get<DataResponse<Task>>(`/tasks/${taskUuid}`),
  create: (data: TaskCreate) => api.post<DataResponse<Task>>("/tasks", data),
  update: (taskUuid: string, data: TaskUpdate) => api.put<DataResponse<Task>>(`/tasks/${taskUuid}`, data),
  delete: (taskUuid: string) => api.delete(`/tasks/${taskUuid}`),
  execute: (taskUuid: string) => api.post<DataResponse<Task>>(`/tasks/${taskUuid}/execute`),
  /** Kick off task execution — returns { operation_id, task_uuid } (HTTP 202). */
  executeStream: (taskUuid: string) =>
    api.post<{ operation_id: string; task_uuid: string }>(`/tasks/${taskUuid}/execute/stream`),
  cancel: (taskUuid: string, data?: TaskCancelRequest) =>
    api.post<DataResponse<Task>>(`/tasks/${taskUuid}/cancel`, data || {}),
  provideInput: (taskUuid: string, data: TaskUserInput) =>
    api.post<DataResponse<Task>>(`/tasks/${taskUuid}/input`, data),
  /** Resume a waiting task with user input — returns { operation_id, task_uuid } (HTTP 202). */
  provideInputStream: (taskUuid: string, data: TaskUserInput) =>
    api.post<{ operation_id: string; task_uuid: string }>(`/tasks/${taskUuid}/input/stream`, data),
  followUpStream: (taskUuid: string, message: string) => api.stream(`/tasks/${taskUuid}/followup/stream`, { message }),
  /** Continue a completed/failed task — returns { operation_id, task_uuid } (HTTP 202). */
  continueStream: (taskUuid: string, goal: string) =>
    api.post<{ operation_id: string; task_uuid: string }>(`/tasks/${taskUuid}/continue/stream`, { goal }),
};
